<template>
  <AdminLayout>
    <div class="ph">
      <div class="pdate">{{ date }}</div>
      <div class="ptitle">Care Registry <span>Dashboard</span></div>
    </div>
    <!-- 01 REGISTRY -->
    <div id="registry" class="sec">
      <LoadingSpinner :isLoading="isLoading" />
      <div class="slbl">01 · Registry Pulse</div>

      <!-- TOP CARDS -->
      <div class="g g5">
        <div v-for="(card, i) in registryTopCards" :key="'top-' + i" class="card">
          <div class="ca" :style="{ background: card.color }"></div>

          <div class="clbl">
            {{ card.title }}
            <span v-if="card.badge" :class="card.badgeClass">{{ card.badge }}</span>
          </div>

          <div class="cn" :style="card.valueStyle || {}">
            {{ card.value }}
          </div>

          <div class="cs">{{ card.subtitle }}</div>
          <div class="cd" :class="card.changeClass">{{ card.change }}</div>
        </div>
      </div>

      <!-- MIDDLE CARDS -->
      <div class="g g3">
        <div v-for="(card, i) in registryMidCards" :key="'mid-' + i" class="card">
          <div class="ca" :style="{ background: card.color }"></div>

          <div class="clbl">{{ card.title }}</div>

          <div class="cn md" :style="card.valueStyle || {}">
            {{ card.value }}
            <span v-if="card.suffix" :style="card.suffixStyle">
              {{ card.suffix }}
            </span>
          </div>

          <div class="cs">{{ card.subtitle }}</div>

          <div v-if="card.change" class="cd" :class="card.changeClass">
            {{ card.change }}
          </div>
        </div>
      </div>

      <!-- CHART SECTION -->
      <div class="g g2">
        <!-- DONUT -->
        <div class="card">
          <div class="clbl" style="margin-bottom: 12px;">
            Tier Distribution — {{ totalIssuedIds }} Care IDs
          </div>

          <div style="display:flex;align-items:center;gap:20px;flex-wrap:wrap;">
            <div
              style="position:relative;width:140px;height:140px;flex-shrink:0;display:flex;align-items:center;justify-content:center;">
              <canvas ref="tierDonut" width="140" height="140"></canvas>
              <div style="position:absolute;text-align:center;pointer-events:none;">
                <div style="font-size:20px;font-weight:600;color:#1A2E22;">
                  {{ totalIssuedIds }}
                </div>
                <div style="font-size:10px;color:#6B8F78;">issued</div>
              </div>
            </div>

            <div style="flex:1;min-width:130px;">
              <div v-for="(t, i) in tiers" :key="'tier-' + i" class="trow">
                <span class="tlbl" :style="{ color: t.color }">
                  {{ t.label }}
                </span>

                <div class="ttrk">
                  <div class="tfil" :id="t.id" :style="{ width: t.width, background: t.color }"></div>
                </div>

                <span class="tpct">{{ t.percent }}</span>
              </div>

              <div
                style="margin-top:10px;padding-top:10px;border-top:1px solid #E2EDE7;font-size:10px;color:#6B8F78;line-height:1.6;">
                Bronze 0–250 · Silver 251–550 · Gold 551–850
              </div>
            </div>
          </div>
        </div>

        <!-- LINE CHART -->
        <div class="card">
          <div class="clbl" style="margin-bottom:4px;">
            Registry Growth — Last 8 Weeks
          </div>

          <div class="leg">
            <span><i style="background:#17865F;"></i>Registered</span>
            <span>
              <i
                style="background:transparent;border-top:2px dashed #B8860B;width:14px;height:0;border-radius:0;margin-top:5px;"></i>
              Care IDs
            </span>
          </div>

          <div class="cw" style="height:180px;">
            <canvas ref="growthLine"
              style="display: block; box-sizing: border-box; height: 180px; width: 541px;"></canvas>

          </div>
        </div>
      </div>
    </div>

    <!-- 02 OPS -->
    <div id="ops" class="sec">
      <div class="slbl">02 · Ops Verification Queue</div>
      <div class="g g4">
        <div v-for="(card, i) in opsCards" :key="'ops-' + i" class="card">
          <div class="ca" :style="{ background: card.color }"></div>

          <div class="clbl">
            {{ card.title }}
            <span v-if="card.badge" :class="card.badgeClass">
              {{ card.badge }}
            </span>
          </div>

          <div class="cn md">
            {{ card.value }}
          </div>

          <div class="cs">
            {{ card.subtitle }}
          </div>

          <div v-if="card.change" class="cd" :class="card.changeClass">
            {{ card.change }}
          </div>
        </div>
      </div>
      <div class="g g32">
        <div class="card">
          <div class="clbl" style="margin-bottom:10px;">Verification Queue — Top 5 <span class="pl pm">Oldest
              first</span></div>
          <div class="tbl-scroll">
            <table class="ot" style="min-width:440px;">
              <thead>
                <tr>
                  <th>Care Giver</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th>Days</th>
                  <th>Bottleneck</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="(item, index) in queue" :key="index">

                  <!-- NAME + CODE -->
                  <td>
                    <div class="on">{{ item.name }}</div>
                    <div class="or">{{ item.code }}</div>
                  </td>

                  <!-- SUBMITTED -->
                  <td style="color:#6B8F78;font-size:11px;">
                    {{ item.submitted }}
                  </td>

                  <!-- STATUS -->
                  <td>
                    <span :class="item.statusClass">
                      {{ item.status }}
                    </span>
                  </td>

                  <!-- DAYS -->
                  <td :style="item.daysStyle">
                    {{ item.days }}
                  </td>

                  <!-- BOTTLENECK -->
                  <td :style="item.bottleneckStyle">
                    {{ item.bottleneck }}
                  </td>

                  <!-- ACTION -->
                  <td>
                    <button class="review-btn" @click="openVerify(item)">
                      Review →
                    </button>
                  </td>

                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="card">
          <div class="clbl" style="margin-bottom:10px;">Bottleneck Signals — Avg. Days</div>
          <div class="cw" style="height:180px;"><canvas ref="bottleneckBar" width="852" height="360"
              style="display: block; box-sizing: border-box; height: 180px; width: 426px;"></canvas></div>
          <div style="font-size:10px;color:#6B8F78;margin-top:8px;font-style:italic;">Average days per verification step
          </div>
        </div>
      </div>
    </div>

    <!-- 03 TRUST -->
    <div id="trust" class="sec">
      <div class="slbl">03 · Trust Score Health</div>
      <div class="g g3">
        <div v-for="(card, i) in trustCards" :key="i" class="card">
          <div class="ca" :style="{ background: card.color }"></div>

          <div class="clbl">
            {{ card.title }}
            <span v-if="card.badge" :class="card.badgeClass">
              {{ card.badge }}
            </span>
          </div>

          <div class="cn md" :style="card.valueStyle">
            {{ card.value }}
          </div>

          <div class="cs">{{ card.subtitle }}</div>

          <div v-if="card.change" :class="card.changeClass">
            {{ card.change }}
          </div>
        </div>
      </div>
      <div class="g g2">
        <div class="card">
          <div class="clbl" style="margin-bottom:10px;">
            Signal Completion Rate — {{ totalIssuedIds }} Issued IDs
          </div>

          <div v-for="(item, i) in signalCompletion" :key="i" class="sigr">
            <div>
              <div class="sn2">{{ item.title }}</div>
              <div class="sm">API · Verified</div>
            </div>

            <div style="display:flex;align-items:center;gap:8px;">
              <div class="sb">
                <div class="sbf" :style="{
                  width: item.percent + '%',
                  background: item.color
                }"></div>
              </div>

              <span class="spc" :style="{ color: item.color }">
                {{ item.displayPercent }}%
              </span>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="clbl" style="margin-bottom:10px;">
            Score Distribution — aligned to tier bands
          </div>

          <div class="cw" style="height:195px;">
            <canvas ref="scoreHist"></canvas>
          </div>
        </div>
      </div>
    </div>


    <!-- 04 EMPLOYER -->
    <div id="employer" class="sec">
      <div class="slbl">04 · Employer Activity</div>
      <div class="g g3">
        <div class="card">
          <div class="ca" style="background:#2563EB;"></div>
          <div class="clbl">Care ID Lookups</div>
          <div class="cn md">89</div>
          <div class="cs">Total verifications</div>
          <div class="cd du">↑ 12 this week</div>
        </div>
        <div class="card">
          <div class="ca" style="background:#F59CAB;"></div>
          <div class="clbl">Reports Sold <span class="pl pg">₦50k</span></div>
          <div class="cn md">14</div>
          <div class="cs">Detailed reports</div>
          <div class="cd du">↑ 3 this week</div>
        </div>
        <div class="card">
          <div class="ca" style="background:#17865F;"></div>
          <div class="clbl">Revenue · May 2026</div>
          <div class="cn md" style="color:#17865F;">₦700k</div>
          <div class="cs">Detailed reports revenue</div>
          <div class="cd du">↑ ₦150k vs Apr</div>
        </div>
      </div>
      <div class="g g2">
        <div class="card">
          <div class="clbl" style="margin-bottom:10px;">Monthly Revenue — ₦</div>
          <div class="cw" style="height:180px;"><canvas ref="revBar" width="1083" height="360"
              style="display: block; box-sizing: border-box; height: 180px; width: 541px;"></canvas></div>
        </div>
        <div class="card">
          <div class="clbl" style="margin-bottom:4px;">Lookups & Reports — Weekly</div>
          <div class="leg"><span><i style="background:#2563EB;"></i>Lookups</span><span><i
                style="background:transparent;border-top:2px dashed #F59CAB;width:14px;height:0;border-radius:0;margin-top:5px;"></i>Reports</span>
          </div>
          <div class="cw" style="height:160px;"><canvas ref="lookupsLine" width="1083" height="320"
              style="display: block; box-sizing: border-box; height: 160px; width: 541px;"></canvas></div>
        </div>
      </div>
    </div>

    <div id="alerts" class="sec">
      <div class="slbl">05 · Alerts — Action Required</div>

      <div class="g g21">

        <!-- LEFT: ALERTS -->
        <div class="card">
          <div class="clbl" style="margin-bottom:10px;">
            Open Items <span class="pl pr">{{ alerts.length }}</span>
          </div>

          <div v-for="(alert, index) in alerts" :key="index" class="ai">
            <div class="adot" :style="{ background: alert.color }"></div>

            <div style="flex:1;">
              <div class="at">
                <strong>{{ alert.title }}</strong> — {{ alert.message }}
              </div>

              <div class="am">
                {{ alert.code }} · {{ alert.meta }}
              </div>
            </div>

            <button class="acta" :class="alert.actionType">
              {{ alert.action }}
            </button>
          </div>
        </div>

        <!-- RIGHT SIDE -->
        <div style="display:flex;flex-direction:column;gap:10px;">

          <!-- API STATUS -->
          <div class="card">
            <div class="clbl" style="margin-bottom:10px;">API Status</div>

            <div v-for="(api, index) in apiStatus" :key="index" class="arow">
              <span class="an">{{ api.name }}</span>
              <span :class="api.stateClass">{{ api.state }}</span>
            </div>
          </div>

          <!-- ENGINE STATUS -->
          <div class="card">
            <div class="clbl" style="margin-bottom:10px;">Engine Status</div>

            <div v-for="(item, index) in engineStatus" :key="index" class="arow">
              <span class="an">{{ item.label }}</span>
              <span :style="item.style">
                {{ item.value }} <span :style="item.dividerStyle">{{ item.divider }}</span> <span
                  :style="item.styleTotal">
                  {{ item.total }}
                </span>
              </span>
            </div>
          </div>

        </div>

      </div>
    </div>
  </AdminLayout>

</template>

<script lang="js">
import AdminLayout from '../../Layouts/AdminLayout.vue';
import Chart from 'chart.js/auto'
import Request from "@/utils/apiclient.js";
// import { formatErrorMessages } from "@/utils/errorHandler";
// import ToastManagerVue from "@/components/ToastAlert/ToastManager.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";

export default {
  components: {
    AdminLayout,
    LoadingSpinner,

  },

  data() {
    return {
      date: '',
      isLoading: false,

      registryData: null,

      registryTopCards: [],
      registryMidCards: [],
      tiers: [],

      tierDonutChart: null,
      growthLineChart: null,
      queue: [],


      opsData: null,
      opsCards: [],
      bottleneckChart: null,

      trust: null,

      trustCards: [],
      signalCompletion: [],

      scoreDistribution: {
        labels: [],
        values: []
      },


      signals: [
        {
          title: "BVN Verified",
          source: "API · Prembly",
          percent: 94,
          color: "#17865F",
          textColor: "#17865F",
        },
        {
          title: "NIN Verified",
          source: "API · NIMC",
          percent: 89,
          color: "#17865F",
          textColor: "#17865F",
        },
        {
          title: "Criminal Check Clear",
          source: "Manual · NPF",
          percent: 78,
          color: "#F5A623",
          textColor: "#A06010",
        },
        {
          title: "Guarantor Verified",
          source: "Manual · VerifyMe",
          percent: 72,
          color: "#F5A623",
          textColor: "#A06010",
        },
        {
          title: "Certificate Verified",
          source: "Manual · WAEC / NECO",
          percent: 61,
          color: "#9CB8A6",
          textColor: "#6B8F78",
        },
      ],

      employerCards: [
        {
          color: "#2563EB",
          title: "Care ID Lookups",
          value: 89,
          subtitle: "Total verifications",
          change: "↑ 12 this week",
          changeClass: "du",
        },
        {
          color: "#F59CAB",
          title: "Reports Sold",
          badge: "₦50k",
          badgeClass: "pl pg",
          value: 14,
          subtitle: "Detailed reports",
          change: "↑ 3 this week",
          changeClass: "du",
        },
        {
          color: "#17865F",
          title: "Revenue · May 2026",
          value: "₦700k",
          valueStyle: { color: "#17865F" },
          subtitle: "Detailed reports revenue",
          change: "↑ ₦150k vs Apr",
          changeClass: "du",
        },
      ],
      alerts: [
        {
          color: "#E74C3C",
          title: "DNE · Chidinma Okafor",
          message: "Criminal record confirmed. Formal rejection pending.",
          code: "SC-2026-100023",
          meta: "Flagged 2 days ago",
          action: "Reject",
          actionType: "d"
        },
        {
          color: "#E74C3C",
          title: "DNE · Uche Obi",
          message: "Criminal record confirmed. Awaiting ops sign-off.",
          code: "SC-2026-100019",
          meta: "Flagged 4 days ago",
          action: "Reject",
          actionType: "d"
        },
        {
          color: "#E74C3C",
          title: "Forged Cert · Emeka Nwachukwu",
          message: "WAEC cert flagged as fraudulent.",
          code: "SC-2026-100031",
          meta: "Flagged yesterday",
          action: "Review",
          actionType: ""
        },
        {
          color: "#F5A623",
          title: "Overdue · Yetunde Bello",
          message: "6 days in queue. NPF report not received. SLA breached.",
          code: "SC-2026-100017",
          meta: "6 days open",
          action: "Chase",
          actionType: ""
        },
        {
          color: "#F5A623",
          title: "Guarantor Unreachable · Folake Adesanya",
          message: "3 call attempts. No response.",
          code: "SC-2026-100051",
          meta: "Attempt 3 of 3",
          action: "Escalate",
          actionType: ""
        }
      ],

      /**
       * 🔵 API STATUS
       */
      apiStatus: [
        { name: "Prembly · BVN", state: "Operational", stateClass: "aok" },
        { name: "NIMC · NIN", state: "Operational", stateClass: "aok" },
        { name: "Prembly · Gov ID", state: "⚠ Degraded", stateClass: "awk" },
        { name: "VerifyMe · Guarantor", state: "Operational", stateClass: "aok" },
        { name: "Paystack", state: "Operational", stateClass: "aok" }
      ],

      /**
       * 🟢 ENGINE STATUS
       */
      engineStatus: [
        {
          label: "Drop-off point",
          value: "Documents step",
          style: {
            fontSize: "11px",
            fontWeight: "600",
            color: "#A06010"
          }
        },
        {
          label: "Avg. Trust Score",
          value: "542",
          divider: "/",
          dividerStyle: {
            fontSize: "10px",
            color: "#6B8F78"
          },
          total: "850",
          styleTotal: {
            fontSize: "10px",
            color: "#6B8F78"
          },
          style: {
            fontSize: "14px",
            fontWeight: "600",
            color: "#1A2E22"
          }
        },

      ],

      // NAV
      navCollapsed: false,
      navOpen: false,
      activeNav: null,

      // TIER BARS
      tgWidth: '0%',
      tsWidth: '0%',
      tbWidth: '0%',


      // COLORS
      G: '#17865F',
      GD: '#B8860B',
      SL: '#4A7A5A',
      BZ: '#A0522D',
      BL: '#2563EB',
      PK: '#F59CAB',
      GR: '#E2EDE7',
    }
  },

  computed: {
    totalIssuedIds() {
      return (
        this.trust?.signal_completion?.reduce(
          (sum, i) => sum + (i.percent > 0 ? 1 : 0),
          0
        ) || 0
      );
    }
  },

  methods: {
    tick() {
      const n = new Date()
      this.date = n.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    },
    mapCaregiverQueue(items) {
      return items.map((item) => {
        const status = (item.status || "").toLowerCase();

        return {
          id: item.id || "-",
          name: item.name || "Unnamed",
          code: item.care_id || "—",
          submitted: item.submitted || "—",

          status: this.statusLabel(status),
          statusClass: this.statusClass(status),

          days: item.days || "—",
          daysStyle: this.daysStyle(status),

          bottleneck: item.bottleneck || "—",
          bottleneckStyle: this.bottleneckStyle(status),
        };
      });
    },
    statusLabel(status) {
      if (status === "pending") return "Pending";
      if (status === "in_progress") return "In progress";
      if(status === "in_review") return "In Review";
      if (status === "complete") return "Complete";
      return "Unknown";
    },

    statusClass(status) {
      if (status === "pending") return "pl pw";
      if (status === "in_progress") return "pl pi";
      if(status === "in_review") return "pl pi"
      if (status === "complete") return "pl pg";
      return "pl";
    },
    daysStyle(status) {
      if (status === "complete") {
        return { color: "#6B8F78" };
      }

      return {
        fontWeight: "600",
        color: "#A06010",
      };
    },

    bottleneckStyle(status) {
      if (status === "complete") {
        return {
          fontSize: "11px",
          color: "#17865F",
          fontWeight: "600",
        };
      }

      return {
        fontSize: "11px",
        color: "#6B8F78",
      };
    },

    safeDestroyChart(chart) {
      if (chart && typeof chart.destroy === "function") {
        chart.destroy();
      }
    },

    // =========================================
    // REGISTRY TOP CARDS
    // =========================================
    mapRegistryTopCards(data) {
      return [
        {
          color: "#17865F",
          title: "Registered",
          value: data.registered || 0,
          subtitle: "Total care givers",

          change: `↑ ${data.registered || 0} total`,
          changeClass: "du",
        },

        {
          color: "#17865F",
          title: "Care IDs Issued",

          badge: "Live",
          badgeClass: "pl pg",

          value: data.verified || 0,
          subtitle: "Verified & active",

          change: `${data.conversion_rate || 0}% conversion`,
          changeClass: "du",
        },

        {
          color: "#B8860B",

          title: "Gold Tier",

          badge: "551–850",
          badgeClass: "pl py",

          value: data.gold || 0,

          valueStyle: {
            color: "#B8860B",
          },

          subtitle: "Premium · fully verified",

          change: `↑ ${data.tier_distribution?.gold?.percent || 0}% share`,
          changeClass: "du",
        },

        {
          color: "#4A7A5A",

          title: "Silver Tier",

          badge: "251–550",
          badgeClass: "pl pg",

          value: data.silver || 0,

          valueStyle: {
            color: "#4A7A5A",
          },

          subtitle: "Deployment-ready",

          change: `↑ ${data.tier_distribution?.silver?.percent || 0}% share`,
          changeClass: "du",
        },

        {
          color: "#A0522D",

          title: "Bronze Tier",

          badge: "0–250",
          badgeClass: "pl pm",

          value: data.bronze || 0,

          valueStyle: {
            color: "#A0522D",
          },

          subtitle: "Partial verification",

          change:
            data.bronze > 0
              ? `↑ ${data.tier_distribution?.bronze?.percent || 0}% share`
              : "→ No change",

          changeClass:
            data.bronze > 0 ? "du" : "df",
        },
      ];
    },

    // =========================================
    // REGISTRY MID CARDS
    // =========================================
    mapRegistryMidCards(data) {
      return [
        {
          color: "#9CB8A6",

          title: "Unverified",

          value: data.unverified || 0,

          valueStyle: {
            color: "#6B8F78",
          },

          subtitle: "Registered · not yet verified",

          change: "→ Awaiting ops",
          changeClass: "df",
        },

        {
          color: "#17865F",

          title: "Conversion Rate",

          value: `${data.conversion_rate || 0}%`,

          valueStyle: {
            color: "#17865F",
          },

          subtitle: "Registered → Care ID issued",

          change: `${data.verified || 0}/${data.registered || 0} verified`,
          changeClass: "du",
        },

        {
          color: "#2563EB",

          title: "Avg. Trust Score",

          value: data.avg_trust_score || 0,

          suffix: "/ 850",

          suffixStyle: {
            fontSize: "11px",
            color: "#6B8F78",
            fontWeight: "400",
          },

          subtitle: `Across ${data.verified || 0} issued IDs`,
        },
      ];
    },

    // =========================================
    // TIER DISTRIBUTION
    // =========================================
    mapTierDistribution(data) {
      return [
        {
          label: "GOLD",

          color: "#B8860B",

          width: `${data.tier_distribution?.gold?.percent || 0
            }%`,

          percent: `${data.tier_distribution?.gold?.percent || 0
            }%`,

          id: "tg",
        },

        {
          label: "SILVER",

          color: "#4A7A5A",

          width: `${data.tier_distribution?.silver?.percent || 0
            }%`,

          percent: `${data.tier_distribution?.silver?.percent || 0
            }%`,

          id: "ts",
        },

        {
          label: "BRONZE",

          color: "#A0522D",

          width: `${data.tier_distribution?.bronze?.percent || 0
            }%`,

          percent: `${data.tier_distribution?.bronze?.percent || 0
            }%`,

          id: "tb",
        },
      ];
    },

    mapTrustCards(data) {
      return [
        {
          color: "#E74C3C",
          title: "DNE Flags Active",
          badge: "Critical",
          badgeClass: "pl pr",
          value: data?.dne_flags ?? 0,
          valueStyle: { color: "#E74C3C" },
          subtitle: "Do Not Employ — criminal record",
        },
        {
          color: "#F5A623",
          title: "Malpractice Open",
          badge: "Review",
          badgeClass: "pl pw",
          value: data?.malpractice_open ?? 0,
          valueStyle: { color: "#A06010" },
          subtitle: "Escalations unresolved",
          change: "↑ 1 new this week",
          changeClass: "cd dd",
        },
        {
          color: "#E74C3C",
          title: "Forged Certs",
          badge: "Flag",
          badgeClass: "pl pr",
          value: data?.forged_certs ?? 0,
          valueStyle: { color: "#E74C3C" },
          subtitle: "Under investigation",
        },
      ];
    },
    mapSignalCompletion(data) {
      const list = data?.signal_completion || [];

      return list.map(item => {
        let color = "#17865F";

        if (item.percent < 70) color = "#9CB8A6";
        else if (item.percent < 85) color = "#F5A623";
        else color = "#17865F";

        return {
          title: item.title,
          percent: Math.min(item.percent, 100),
          displayPercent: item.percent,
          color,
        };
      });
    },

    mapScoreDistribution(data) {
      const dist = data?.score_distribution;

      return {
        labels: dist?.labels || [],
        values: dist?.values || [],
      };
    },
    // =========================================
    // DONUT CHART
    // =========================================
    renderTierDonut() {
      const canvas = this.$refs.tierDonut;
      if (!canvas) return;

      this.safeDestroyChart(this.tierDonutChart);

      // IMPORTANT: clear canvas context
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      this.tierDonutChart = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels: ["Gold", "Silver", "Bronze"],
          datasets: [
            {
              data: [
                this.registryData.tier_distribution.gold.count,
                this.registryData.tier_distribution.silver.count,
                this.registryData.tier_distribution.bronze.count,
              ],
              backgroundColor: ["#B8860B", "#4A7A5A", "#A0522D"],
              borderWidth: 0,
            },
          ],
        },
        options: {
          cutout: "72%",
          plugins: {
            legend: { display: false },
          },
        },
      });
    },

    // =========================================
    // LINE CHART
    // =========================================
    renderGrowthChart() {
      const canvas = this.$refs.growthLine;
      if (!canvas) return;

      this.safeDestroyChart(this.growthLineChart);

      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      this.growthLineChart = new Chart(ctx, {
        type: "line",
        data: {
          labels: this.registryData.growth_chart.labels,
          datasets: [
            {
              label: "Registered",
              data: this.registryData.growth_chart.registered,
              borderColor: "#17865F",
              tension: 0.4,
            },
            {
              label: "Care IDs",
              data: this.registryData.growth_chart.verified,
              borderColor: "#B8860B",
              borderDash: [6, 6],
              tension: 0.4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
        },
      });
    },
    renderBottleneckChart() {
      if (!this.opsData || !this.opsData.bottleneck_chart) return;

      const canvas = this.$refs.bottleneckBar;
      if (!canvas) return;

      if (this.bottleneckChart) {
        this.bottleneckChart.destroy();
      }

      const ctx = canvas.getContext("2d");

      this.bottleneckChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: this.opsData.bottleneck_chart.labels,
          datasets: [
            {
              label: "Avg. days",
              data: this.opsData.bottleneck_chart.values,
              backgroundColor: [
                "rgba(231,76,60,.7)",
                "rgba(245,166,35,.7)",
                "rgba(245,166,35,.45)",
                "rgba(23,134,95,.55)",
                "rgba(23,134,95,.55)",
              ],
              borderRadius: 4,
              borderSkipped: false,
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
          },
        },
      });
    },

    renderScoreChart() {
      if (!this.scoreDistribution) return;

      const canvas = this.$refs.scoreHist;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");

      // 🔥 SAFEST CLEANUP (prevents "Canvas already in use")
      const existingChart = Chart.getChart(canvas);
      if (existingChart) {
        existingChart.destroy();
      }

      // fallback cleanup (in case you still store instance)
      if (this.scoreChart) {
        this.scoreChart.destroy();
        this.scoreChart = null;
      }

      this.scoreChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: this.scoreDistribution.labels,
          datasets: [
            {
              label: "Users",
              data: this.scoreDistribution.values,
              backgroundColor: "rgba(23,134,95,.6)",
              borderRadius: 4,
              borderSkipped: false,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
          },
        },
      });
    },

    mapOpsCards(data) {
      return [
        {
          color: "#F5A623",
          title: "Pending",
          badge: "Action",
          badgeClass: "pl pw",
          value: data.pending || 0,
          subtitle: "Awaiting ops",
          change: data.pending
            ? `↑ ${data.pending} in queue`
            : "→ Clear",
          changeClass: data.pending ? "du" : "df",
        },

        {
          color: "#2563EB",
          title: "In Progress",
          value: data.in_progress || 0,
          subtitle: "Verification underway",
          change: data.in_progress
            ? `↑ ${data.in_progress} active`
            : "→ Idle",
          changeClass: data.in_progress ? "du" : "df",
        },

        {
          color: "#17865F",
          title: "Completed Today",
          value: data.completed_today || 0,
          subtitle: "Care IDs issued today",
          change: data.completed_today
            ? `↑ ${data.completed_today} today`
            : "→ None",
          changeClass: data.completed_today ? "du" : "df",
        },

        {
          color: "#9CB8A6",
          title: "Avg. Days to Verify",
          value: data.avg_days_to_verify || 0,
          subtitle: "Submit → Care ID issued",
          change: `~${data.avg_days_to_verify || 0} days`,
          changeClass: "df",
        },
      ];
    },
    openVerify(cg) {
      console.log(cg)
      this.$router.push(`/admin/verification-center/${cg.id}`);
    },

    async fetchAdminDetails() {
      try {

        this.isLoading = true;

        const request = new Request(
          `/admin/dashboard-details/`
        );

        await request.get((res, error) => {

          if (error) {
            console.log("FETCH ERROR", error);
            return;
          }

          console.log("ADMIN DATA", res);

          if (res.ops) {


            this.opsCards = this.mapOpsCards(res.ops);


            this.queue = this.mapCaregiverQueue(res.ops.queue);
            this.registryData = res.registry;

            const data = this.registryData;

            // mappings
            this.registryTopCards =
              this.mapRegistryTopCards(data);

            this.registryMidCards =
              this.mapRegistryMidCards(data);

            this.tiers =
              this.mapTierDistribution(data);

            this.trustCards = this.mapTrustCards(res.trust);
            this.signalCompletion = this.mapSignalCompletion(res.trust);
            this.scoreDistribution = this.mapScoreDistribution(res.trust);

            this.$nextTick(() => {
              this.renderTierDonut();
              this.renderGrowthChart();
              this.renderBottleneckChart();
              this.renderScoreChart();
            });


          }


        });


      } catch (err) {

        console.log("FETCH FAILED", err);

      } finally {

        this.isLoading = false;
      }
    },
    // =======================
    // CHARTS
    // =======================
    initCharts() {

      Chart.defaults.font.family = "'Open Sans',sans-serif"
      Chart.defaults.font.size = 11
      Chart.defaults.color = '#6B8F78'


      // =======================
      // BOTTLENECK BAR
      // =======================
      new Chart(this.$refs.bottleneckBar, {
        type: 'bar',
        data: {
          labels: ['NPF check', 'Guarantor', 'Cert verify', 'BVN API', 'NIN API'],
          datasets: [{
            label: 'Avg. days',
            data: [4.8, 3.2, 2.1, 0.4, 0.3],
            backgroundColor: [
              'rgba(231,76,60,.7)',
              'rgba(245,166,35,.7)',
              'rgba(245,166,35,.45)',
              'rgba(23,134,95,.55)',
              'rgba(23,134,95,.55)'
            ],
            borderRadius: 4,
            borderSkipped: false
          }]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: c => `${c.raw}d avg.`
              }
            }
          },
          scales: {
            x: {
              grid: { color: '#E2EDE7' },
              beginAtZero: true,
              ticks: { font: { size: 10 } }
            },
            y: {
              grid: { display: false },
              ticks: { font: { size: 10 } }
            }
          }
        }
      });

      // =======================
      // SCORE HISTOGRAM
      // =======================
      new Chart(this.$refs.scoreHist, {
        type: 'bar',
        data: {
          labels: [
            '0–250',
            '251–400',
            '401–550',
            '551–650',
            '651–750',
            '751–850'
          ],
          datasets: [
            {
              label: 'Care IDs',
              data: [20, 18, 40, 18, 10, 6],
              backgroundColor: [
                'rgba(160,82,45,.75)',
                'rgba(74,122,90,.5)',
                'rgba(74,122,90,.8)',
                'rgba(184,134,11,.55)',
                'rgba(184,134,11,.75)',
                'rgba(184,134,11,.95)'
              ],
              borderRadius: 4,
              borderSkipped: false
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,

          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (c) => `${c.raw} Care IDs`
              }
            }
          },

          scales: {
            x: {
              grid: { display: false },
              ticks: {
                font: { size: 9 },
                maxRotation: 0
              }
            },
            y: {
              grid: { color: '#E2EDE7' },
              beginAtZero: true,
              ticks: {
                font: { size: 10 },
                stepSize: 10
              }
            }
          }
        }
      });

      // =======================
      // REVENUE BAR
      // =======================
      new Chart(this.$refs.revBar, {
        type: 'bar',
        data: {
          labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
          datasets: [
            {
              label: 'Revenue',
              data: [0, 100000, 250000, 550000, 700000],
              backgroundColor: 'rgba(23,134,95,.7)',
              hoverBackgroundColor: 'rgba(23,134,95,.9)',
              borderRadius: 5,
              borderSkipped: false
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,

          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (c) => `₦${Math.round(c.raw / 1000)}k`
              }
            }
          },

          scales: {
            x: {
              grid: { display: false },
              ticks: { font: { size: 10 } }
            },
            y: {
              grid: { color: '#E2EDE7' },
              beginAtZero: true,
              ticks: {
                font: { size: 10 },
                callback: (v) => '₦' + (v / 1000) + 'k'
              }
            }
          }
        }
      });

      // =======================
      // LOOKUPS LINE
      // =======================
      new Chart(this.$refs.lookupsLine, {
        type: 'line',
        data: {
          labels: [
            'Wk 1', 'Wk 2', 'Wk 3', 'Wk 4',
            'Wk 5', 'Wk 6', 'Wk 7', 'Wk 8'
          ],
          datasets: [
            {
              label: 'Lookups',
              data: [2, 5, 9, 14, 20, 28, 38, 51],
              borderColor: '#2563EB',
              backgroundColor: 'rgba(37,99,235,.07)',
              borderWidth: 2,
              tension: 0.4,
              fill: true,
              pointBackgroundColor: '#2563EB',
              pointRadius: 3,
              pointHoverRadius: 5
            },
            {
              label: 'Reports',
              data: [0, 1, 2, 3, 5, 7, 10, 14],
              borderColor: '#F59CAB',
              backgroundColor: 'transparent',
              borderWidth: 1.5,
              tension: 0.4,
              pointBackgroundColor: '#F59CAB',
              pointRadius: 3,
              pointHoverRadius: 5,
              borderDash: [5, 3]
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,

          plugins: {
            legend: { display: false },
            tooltip: {
              mode: 'index',
              intersect: false
            }
          },

          scales: {
            x: {
              grid: { color: '#E2EDE7' },
              ticks: { font: { size: 10 } }
            },
            y: {
              grid: { color: '#E2EDE7' },
              beginAtZero: true,
              ticks: { font: { size: 10 } }
            }
          }
        }
      });
    }
  },

  mounted() {

    // CLOCK
    this.tick()

    this.timer = setInterval(() => {
      this.tick()
    }, 1000)

    // TIER BARS
    setTimeout(() => {
      this.tgWidth = '30%'
      this.tsWidth = '52%'
      this.tbWidth = '18%'
    }, 400)

    // CHARTS
    this.$nextTick(() => {
      this.initCharts()
    })

    this.fetchAdminDetails();
    
  },

  beforeUnmount() {
    clearInterval(this.timer);

    if (this.charts && this.charts.length) {
      this.charts.forEach(chart => {
        chart?.destroy?.();
      });
    }
  }

}

</script>


<style lang="css" scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html {
  scroll-behavior: smooth;
}

body {
  font-family: 'Open Sans', sans-serif;
  background: #F4F7F5;
  color: #1A2E22;
  font-size: 13px;
  display: flex;
  min-height: 100vh;
}

/* ── SIDE NAV ── */
.sidenav {
  width: 220px;
  background: #0D5C40;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 200;
  transition: width .25s ease, transform .25s ease;
}

.sidenav.collapsed {
  width: 58px;
}

.sn-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 14px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, .08);
  min-height: 60px;
  overflow: hidden;
}

.sn-mark {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: #F59CAB;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.sn-brand-name {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #fff;
  white-space: nowrap;
}

.sn-brand-sub {
  font-size: 8px;
  color: rgba(255, 255, 255, .35);
  letter-spacing: 1px;
  margin-top: 1px;
  white-space: nowrap;
}

.sidenav.collapsed .sn-brand-name,
.sidenav.collapsed .sn-brand-sub {
  display: none;
}

.sn-sec {
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, .28);
  padding: 12px 14px 4px;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
}

.sidenav.collapsed .sn-sec {
  opacity: 0;
  height: 0;
  padding: 0;
}

.sn-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  cursor: pointer;
  color: rgba(255, 255, 255, .5);
  font-size: 12px;
  font-weight: 500;
  border-left: 3px solid transparent;
  transition: .15s;
  text-decoration: none;
  overflow: hidden;
}

.sn-item:hover {
  background: rgba(255, 255, 255, .06);
  color: rgba(255, 255, 255, .85);
}

.sn-item.active {
  background: rgba(255, 255, 255, .1);
  color: #fff;
  border-left-color: #F59CAB;
}

.sn-icon {
  font-size: 15px;
  flex-shrink: 0;
  width: 22px;
  text-align: center;
}

.sn-lbl {
  white-space: nowrap;
  flex: 1;
}

.sidenav.collapsed .sn-lbl {
  display: none;
}

.sn-badge {
  font-size: 9px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 20px;
  background: rgba(231, 76, 60, .3);
  color: #FCA5A5;
  white-space: nowrap;
}

.sidenav.collapsed .sn-badge {
  display: none;
}

.review-btn {
  font-size: 11px;
  font-weight: 600;
  padding: 5px 14px;
  border-radius: 7px;
  border: 1px solid var(--border);
  background: white;
  color: var(--dkgreen);
  cursor: pointer;
  transition: 0.2s;
}

.sn-footer {
  margin-top: auto;
  padding: 12px 14px;
  border-top: 1px solid rgba(255, 255, 255, .08);
}

.sn-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, .3);
  font-size: 11px;
  cursor: pointer;
  font-family: 'Open Sans', sans-serif;
  padding: 0;
  width: 100%;
}

.sn-toggle:hover {
  color: rgba(255, 255, 255, .7);
}

.sidenav.collapsed .sn-toggle span {
  display: none;
}

/* ── OVERLAY ── */
.overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .45);
  z-index: 150;
}

.overlay.show {
  display: block;
}

/* ── MAIN AREA ── */
.layout {
  margin-left: 220px;
  flex: 1;
  display: flex;
  flex-direction: column;
  transition: margin-left .25s ease;
  min-width: 0;
}

.layout.nav-sm {
  margin-left: 58px;
}

.layout.nav-0 {
  margin-left: 0;
}

/* ── TOPBAR ── */
.topbar {
  background: #0D5C40;
  display: flex;
  align-items: center;
  padding: 0 18px;
  height: 50px;
  gap: 12px;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 6px rgba(13, 92, 64, .18);
}

.tb-burger {
  display: none;
  background: none;
  border: none;
  color: rgba(255, 255, 255, .7);
  font-size: 20px;
  cursor: pointer;
  line-height: 1;
  padding: 2px 4px;
}

.tb-title {
  font-size: 11px;
  color: rgba(255, 255, 255, .4);
}

.tb-r {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 11px;
}

.live {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 600;
  color: rgba(255, 255, 255, .6);
}

.live::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4ADE80;
  animation: lp 2s infinite;
}

@keyframes lp {

  0%,
  100% {
    opacity: 1
  }

  50% {
    opacity: .35
  }
}

.ttime {
  font-size: 11px;
  color: rgba(255, 255, 255, .32);
}

.abadge {
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 1px;
  padding: 3px 9px;
  border-radius: 20px;
  background: rgba(245, 156, 171, .18);
  color: #F59CAB;
  border: 1px solid rgba(245, 156, 171, .3);
}

/* ── CONTENT ── */
.main {
  padding: 18px 20px 48px;
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
}

.ph {
  margin-bottom: 14px;
}

.pdate {
  font-size: 10px;
  color: #6B8F78;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 2px;
}

.ptitle {
  font-size: 18px;
  font-weight: 600;
  color: #1A2E22;
}

.ptitle span {
  color: #17865F;
}

.slbl {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #6B8F78;
  margin: 16px 0 9px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.slbl::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #E2EDE7;
}

.g {
  display: grid;
  gap: 10px;
  margin-bottom: 10px;
}

.g5 {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.g4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.g3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.g2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.g32 {
  grid-template-columns: 3fr 2fr;
}

.g21 {
  grid-template-columns: 2fr 1fr;
}

.card {
  background: #fff;
  border: 1px solid #E2EDE7;
  border-radius: 12px;
  padding: 15px 17px;
  position: relative;
  overflow: hidden;
}

.ca {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
}

.clbl {
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 1.5px;
  color: #6B8F78;
  margin-bottom: 7px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px;
}

.cn {
  font-size: 30px;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 3px;
}

.cn.md {
  font-size: 24px;
}

.cs {
  font-size: 11px;
  color: #6B8F78;
}

.cd {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 600;
  margin-top: 5px;
  padding: 2px 8px;
  border-radius: 20px;
}

.du {
  color: #17865F;
  background: #E8F4EF;
}

.dd {
  color: #E74C3C;
  background: #FEF2F2;
}

.df {
  color: #6B8F78;
  background: #F4F7F5;
}

.pl {
  font-size: 9px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 20px;
}

.pg {
  background: #E8F4EF;
  color: #17865F;
  border: 1px solid #D0E3D8;
}

.pr {
  background: #FEF2F2;
  color: #E74C3C;
  border: 1px solid rgba(231, 76, 60, .2);
}

.pw {
  background: #FFF8EC;
  color: #A06010;
  border: 1px solid rgba(245, 166, 35, .25);
}

.pi {
  background: #EFF6FF;
  color: #2563EB;
  border: 1px solid rgba(37, 99, 235, .2);
}

.pm {
  background: #F4F7F5;
  color: #6B8F78;
  border: 1px solid #E2EDE7;
}

.py {
  background: #FFF9E6;
  color: #B8860B;
  border: 1px solid rgba(184, 134, 11, .22);
}

.trow {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 7px;
}

.trow:last-child {
  margin-bottom: 0;
}

.tlbl {
  font-size: 10px;
  font-weight: 600;
  width: 52px;
  flex-shrink: 0;
}

.ttrk {
  flex: 1;
  height: 6px;
  background: #F4F7F5;
  border-radius: 3px;
  overflow: hidden;
  border: 1px solid #E2EDE7;
}

.tfil {
  height: 100%;
  border-radius: 3px;
  transition: width 1.1s cubic-bezier(.16, 1, .3, 1);
}

.tpct {
  font-size: 11px;
  color: #6B8F78;
  width: 28px;
  text-align: right;
}

table.ot {
  width: 100%;
  border-collapse: collapse;
}

table.ot th {
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 1.5px;
  color: #6B8F78;
  padding: 0 0 8px;
  text-align: left;
  border-bottom: 1px solid #E2EDE7;
}

table.ot td {
  font-size: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #E2EDE7;
  vertical-align: middle;
}

table.ot tr:last-child td {
  border: none;
}

.on {
  font-weight: 600;
  color: #1A2E22;
}

.or {
  font-size: 10px;
  color: #6B8F78;
  margin-top: 1px;
}

.sigr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 0;
  border-bottom: 1px solid #E2EDE7;
}

.sigr:last-child {
  border: none;
}

.sn2 {
  font-size: 12px;
  font-weight: 600;
  color: #1A2E22;
}

.sm {
  font-size: 10px;
  color: #6B8F78;
  margin-top: 1px;
}

.sb {
  width: 56px;
  height: 4px;
  background: #F4F7F5;
  border-radius: 2px;
  overflow: hidden;
}

.sbf {
  height: 100%;
  border-radius: 2px;
}

.spc {
  font-size: 11px;
  font-weight: 600;
  width: 28px;
  text-align: right;
}

.ai {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 9px 0;
  border-bottom: 1px solid #E2EDE7;
}

.ai:last-child {
  border: none;
}

.adot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 3px;
}

.at {
  font-size: 12px;
  color: #1A2E22;
  line-height: 1.45;
  flex: 1;
}

.at strong {
  font-weight: 600;
}

.am {
  font-size: 10px;
  color: #6B8F78;
  margin-top: 2px;
}

.acta {
  font-size: 10px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid #E2EDE7;
  background: #fff;
  color: #1A2E22;
  flex-shrink: 0;
  font-family: 'Open Sans', sans-serif;
  transition: .15s;
}

.acta:hover {
  background: #F4F7F5;
}

.acta.d {
  color: #E74C3C;
  border-color: rgba(231, 76, 60, .25);
}

.acta.d:hover {
  background: #FEF2F2;
}

.arow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #E2EDE7;
}

.arow:last-child {
  border: none;
}

.an {
  font-size: 12px;
  font-weight: 600;
}

.aok {
  font-size: 10px;
  font-weight: 600;
  color: #17865F;
  display: flex;
  align-items: center;
  gap: 4px;
}

.aok::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4ADE80;
}

.awk {
  font-size: 10px;
  font-weight: 600;
  color: #A06010;
}

.leg {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 11px;
  color: #6B8F78;
}

.leg span {
  display: flex;
  align-items: center;
  gap: 5px;
}

.leg i {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
  display: inline-block;
}

.cw {
  position: relative;
  width: 100%;
}

.sec {
  scroll-margin-top: 68px;
}

.tbl-scroll {
  overflow-x: auto;
}

/* ── RESPONSIVE ── */
@media(max-width:1060px) {
  .g5 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .g4 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .g32,
  .g21 {
    grid-template-columns: 1fr;
  }
}

@media(max-width:860px) {
  .sidenav {
    width: 58px;
  }

  .sidenav .sn-brand-name,
  .sidenav .sn-brand-sub,
  .sidenav .sn-sec,
  .sidenav .sn-lbl,
  .sidenav .sn-badge,
  .sidenav .sn-toggle span {
    display: none;
  }

  .sidenav .sn-sec {
    opacity: 0;
    height: 0;
    padding: 0;
  }

  .layout {
    margin-left: 58px;
  }

  .layout.nav-0 {
    margin-left: 0;
  }
}

@media(max-width:600px) {
  .sidenav {
    transform: translateX(-100%);
    width: 220px;
  }

  .sidenav.mob-open {
    transform: translateX(0);
  }

  .sidenav.mob-open .sn-brand-name,
  .sidenav.mob-open .sn-brand-sub,
  .sidenav.mob-open .sn-sec,
  .sidenav.mob-open .sn-lbl,
  .sidenav.mob-open .sn-badge,
  .sidenav.mob-open .sn-toggle span {
    display: block;
  }

  .sidenav.mob-open .sn-sec {
    opacity: 1;
    height: auto;
    padding: 12px 14px 4px;
  }

  .layout,
  .layout.nav-sm {
    margin-left: 0;
  }

  .tb-burger {
    display: block;
  }

  .g5,
  .g4,
  .g3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .g2,
  .g32,
  .g21 {
    grid-template-columns: 1fr;
  }

  .main {
    padding: 12px 12px 36px;
  }
}

@media(max-width:360px) {

  .g5,
  .g4,
  .g3,
  .g2 {
    grid-template-columns: 1fr;
  }
}
</style>